// ระบบจัดการรายได้ของระบบและน้อง
// เพื่อให้เจ้าของระบบสามารถติดตามรายได้ได้อย่างชัดเจน

import { collection, addDoc, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * บันทึกรายได้ของระบบจากการจอง
 * @param {object} bookingData - ข้อมูลการจอง
 * @param {object} pricingInfo - ข้อมูลราคา
 */
export const recordSystemRevenue = async (bookingData, pricingInfo) => {
  try {
    const revenueData = {
      bookingId: bookingData.id,
      userId: bookingData.userId,
      girlId: bookingData.girlId,
      girlName: bookingData.girlName,
      
      // รายได้ของระบบ
      systemRevenue: pricingInfo.systemRevenue,
      commission: pricingInfo.commission,
      
      // ข้อมูลการจอง
      bookingDate: bookingData.bookingDate,
      bookingTime: bookingData.bookingTime,
      duration: pricingInfo.duration,
      
      // ราคารวม
      totalServicePrice: pricingInfo.totalServicePrice,
      totalCoinPrice: pricingInfo.totalCoinPrice,
      
      // สถานะ
      status: 'pending', // pending, confirmed, paid
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'systemRevenue'), revenueData);
    console.log('✅ บันทึกรายได้ของระบบสำเร็จ:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถบันทึกรายได้ของระบบ:', error);
    throw error;
  }
};

/**
 * บันทึกรายได้ของน้องจากการจอง
 * @param {object} bookingData - ข้อมูลการจอง
 * @param {object} pricingInfo - ข้อมูลราคา
 */
export const recordGirlRevenue = async (bookingData, pricingInfo) => {
  try {
    const revenueData = {
      bookingId: bookingData.id,
      userId: bookingData.userId,
      girlId: bookingData.girlId,
      girlName: bookingData.girlName,
      
      // รายได้ของน้อง
      girlRevenue: pricingInfo.girlRevenue,
      
      // ข้อมูลการจอง
      bookingDate: bookingData.bookingDate,
      bookingTime: bookingData.bookingTime,
      duration: pricingInfo.duration,
      
      // ราคารวม
      totalServicePrice: pricingInfo.totalServicePrice,
      totalCoinPrice: pricingInfo.totalCoinPrice,
      
      // สถานะ
      status: 'pending', // pending, confirmed, paid
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'girlRevenue'), revenueData);
    console.log('✅ บันทึกรายได้ของน้องสำเร็จ:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถบันทึกรายได้ของน้อง:', error);
    throw error;
  }
};

/**
 * ดึงรายได้ของระบบในช่วงเวลาที่กำหนด
 * @param {string} startDate - วันที่เริ่มต้น (YYYY-MM-DD)
 * @param {string} endDate - วันที่สิ้นสุด (YYYY-MM-DD)
 * @returns {Promise<Array>} รายการรายได้ของระบบ
 */
export const getSystemRevenue = async (startDate, endDate) => {
  try {
    const q = query(
      collection(db, 'systemRevenue'),
      where('bookingDate', '>=', startDate),
      where('bookingDate', '<=', endDate),
      orderBy('bookingDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const revenues = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return revenues;
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายได้ของระบบ:', error);
    throw error;
  }
};

/**
 * ดึงรายได้ของน้องในช่วงเวลาที่กำหนด
 * @param {string} girlId - ID ของน้อง
 * @param {string} startDate - วันที่เริ่มต้น (YYYY-MM-DD)
 * @param {string} endDate - วันที่สิ้นสุด (YYYY-MM-DD)
 * @returns {Promise<Array>} รายการรายได้ของน้อง
 */
export const getGirlRevenue = async (girlId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, 'girlRevenue'),
      where('girlId', '==', girlId),
      where('bookingDate', '>=', startDate),
      where('bookingDate', '<=', endDate),
      orderBy('bookingDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const revenues = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return revenues;
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายได้ของน้อง:', error);
    throw error;
  }
};

/**
 * คำนวณรายได้รวมของระบบในช่วงเวลาที่กำหนด
 * @param {string} startDate - วันที่เริ่มต้น (YYYY-MM-DD)
 * @param {string} endDate - วันที่สิ้นสุด (YYYY-MM-DD)
 * @returns {Promise<object>} สรุปรายได้ของระบบ
 */
export const calculateTotalSystemRevenue = async (startDate, endDate) => {
  try {
    const revenues = await getSystemRevenue(startDate, endDate);
    
    const summary = {
      totalRevenue: 0,
      totalBookings: revenues.length,
      confirmedRevenue: 0,
      pendingRevenue: 0,
      paidRevenue: 0,
      revenues: revenues
    };
    
    revenues.forEach(revenue => {
      summary.totalRevenue += revenue.systemRevenue;
      
      switch (revenue.status) {
        case 'confirmed':
          summary.confirmedRevenue += revenue.systemRevenue;
          break;
        case 'pending':
          summary.pendingRevenue += revenue.systemRevenue;
          break;
        case 'paid':
          summary.paidRevenue += revenue.systemRevenue;
          break;
      }
    });
    
    return summary;
  } catch (error) {
    console.error('❌ ไม่สามารถคำนวณรายได้รวมของระบบ:', error);
    throw error;
  }
};

/**
 * คำนวณรายได้รวมของน้องในช่วงเวลาที่กำหนด
 * @param {string} girlId - ID ของน้อง
 * @param {string} startDate - วันที่เริ่มต้น (YYYY-MM-DD)
 * @param {string} endDate - วันที่สิ้นสุด (YYYY-MM-DD)
 * @returns {Promise<object>} สรุปรายได้ของน้อง
 */
export const calculateTotalGirlRevenue = async (girlId, startDate, endDate) => {
  try {
    const revenues = await getGirlRevenue(girlId, startDate, endDate);
    
    const summary = {
      totalRevenue: 0,
      totalBookings: revenues.length,
      confirmedRevenue: 0,
      pendingRevenue: 0,
      paidRevenue: 0,
      revenues: revenues
    };
    
    revenues.forEach(revenue => {
      summary.totalRevenue += revenue.girlRevenue;
      
      switch (revenue.status) {
        case 'confirmed':
          summary.confirmedRevenue += revenue.girlRevenue;
          break;
        case 'pending':
          summary.pendingRevenue += revenue.girlRevenue;
          break;
        case 'paid':
          summary.paidRevenue += revenue.girlRevenue;
          break;
      }
    });
    
    return summary;
  } catch (error) {
    console.error('❌ ไม่สามารถคำนวณรายได้รวมของน้อง:', error);
    throw error;
  }
};

/**
 * อัปเดตสถานะการจ่ายเงิน
 * @param {string} revenueId - ID ของรายได้
 * @param {string} type - ประเภท ('system' หรือ 'girl')
 * @param {string} status - สถานะใหม่ ('confirmed', 'paid')
 */
export const updateRevenueStatus = async (revenueId, type, status) => {
  try {
    const collectionName = type === 'system' ? 'systemRevenue' : 'girlRevenue';
    const revenueRef = doc(db, collectionName, revenueId);
    
    await updateDoc(revenueRef, {
      status: status,
      updatedAt: new Date()
    });
    
    console.log(`✅ อัปเดตสถานะรายได้${type === 'system' ? 'ของระบบ' : 'ของน้อง'}สำเร็จ`);
  } catch (error) {
    console.error('❌ ไม่สามารถอัปเดตสถานะรายได้:', error);
    throw error;
  }
};



